import React, { Fragment, useState, useEffect } from 'react';
import { Card, CardBody, Form, FormGroup, Input, Label, Row, Col } from 'reactstrap';
import { H5, H4, Btn } from '../../../../AbstractElements';
import SweetAlert from 'sweetalert2';
import { toast } from 'react-toastify';
import { useForm } from "react-hook-form";
import { UsersCountryMenu, UsersStateMenu, Address, PostalCode, Country, State, City, avisStatusList, avisCarColorList, SaveTxt } from '../../../../Constant';
import TicketPhotoGallery from '../../../../Components/TicketPhotoGallery';
import { getTicketImages } from '../../../../utils/ticketImages';
import { firebase_app, firebase_rdb } from '../../../../Config/Config';
import i18n from '../../../../i18n';
import { confirmAndSaveAdminTicket, saveAdminTicketUpdate } from './adminTicketUpdate';

const CLIENT_ID = 'maxpark-220731';
const AGENT_TICKET_LOOKUP_BATCH_SIZE = 12;
export const TICKET_IMAGE_LIGHTBOX_TYPES = ['image'];
const MANUAL_PAYMENT_METHODS = new Set(['cheque', 'interac']);
let legacyAgentIdsPromise = null;

const getPaymentFormStatus = payment => {
    if (!payment || typeof payment !== 'object' || payment.paid !== true) return 'unpaid';
    if (payment.source === 'manual' && MANUAL_PAYMENT_METHODS.has(payment.method)) {
        return payment.method;
    }
    return 'automatic';
};

const getClientRef = () => firebase_rdb.child('clients').child(CLIENT_ID);

const getAuthenticatedUser = () => new Promise(resolve => {
    const currentUser = firebase_app.auth().currentUser;

    if (currentUser) {
        resolve(currentUser);
        return;
    }

    let resolved = false;
    const unsubscribe = firebase_app.auth().onAuthStateChanged(user => {
        if (resolved) {
            return;
        }

        resolved = true;
        unsubscribe();
        resolve(user);
    });

    setTimeout(() => {
        if (resolved) {
            return;
        }

        resolved = true;
        unsubscribe();
        resolve(firebase_app.auth().currentUser);
    }, 1500);
});

const readLegacyAgentIds = async () => {
    const agentsRef = getClientRef().child('agents');
    const currentUser = await getAuthenticatedUser();

    if (currentUser && typeof fetch === 'function') {
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${agentsRef.toString()}.json?shallow=true&auth=${encodeURIComponent(token)}`);

            if (response.ok) {
                const agents = await response.json();
                return Object.keys(agents || {});
            }
        } catch (error) {
            console.warn('Unable to read shallow agent list; falling back to SDK.', error);
        }
    }

    const agentsSnapshot = await agentsRef.once('value');
    const agentIds = [];

    agentsSnapshot.forEach(agentSnapshot => {
        agentIds.push(agentSnapshot.key);
        return false;
    });

    return agentIds;
};

export const resetLegacyAgentIdsCache = () => {
    legacyAgentIdsPromise = null;
};

const getLegacyAgentIds = async () => {
    if (!legacyAgentIdsPromise) {
        legacyAgentIdsPromise = readLegacyAgentIds().catch(error => {
            legacyAgentIdsPromise = null;
            throw error;
        });
    }

    return legacyAgentIdsPromise;
};

const chunkArray = (items, size) => {
    const chunks = [];

    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }

    return chunks;
};

export const findLegacyAgentTicket = async (ticketId, options = {}) => {
    const clientRef = options.clientRef || getClientRef();
    const agentIds = options.agentIds || await getLegacyAgentIds();
    const batchSize = options.batchSize || AGENT_TICKET_LOOKUP_BATCH_SIZE;

    for (const agentIdBatch of chunkArray(agentIds, batchSize)) {
        const ticketResults = await Promise.all(
            agentIdBatch.map(async agentId => {
                const ticketSnapshot = await clientRef
                    .child('agents')
                    .child(agentId)
                    .child('tickets')
                    .child(ticketId)
                    .once('value');

                if (!ticketSnapshot.exists()) {
                    return null;
                }

                return {
                    agentId,
                    ticket: ticketSnapshot.val(),
                    ref: ticketSnapshot.ref,
                };
            })
        );
        const foundTicket = ticketResults.find(Boolean);

        if (foundTicket) {
            return foundTicket;
        }
    }

    return null;
};

const startLegacyOwnerLookup = (ticketId, findLegacyTicket, onAgentOwnerResolved) => {
    findLegacyTicket(ticketId)
        .then(legacyTicket => {
            if (legacyTicket && legacyTicket.agentId) {
                onAgentOwnerResolved(legacyTicket.agentId, legacyTicket);
            }
        })
        .catch(error => {
            console.warn('Legacy agent ticket owner lookup failed.', error);
        });
};

export const resolveTicketForEdit = async (ticketId, options = {}) => {
    const clientRef = options.clientRef || getClientRef();
    const findLegacyTicket = options.findLegacyTicket || (id => findLegacyAgentTicket(id, { clientRef }));
    const onAgentOwnerResolved = options.onAgentOwnerResolved || (() => {});
    const centralSnapshot = await clientRef.child('tickets').child(ticketId).once('value');

    if (centralSnapshot.exists()) {
        const centralTicket = centralSnapshot.val();
        const centralAgentId = centralTicket.agentUID || centralTicket.agentUid || centralTicket.agentId || centralTicket.agentid || '';

        if (!centralAgentId && options.resolveLegacyOwner !== false) {
            startLegacyOwnerLookup(ticketId, findLegacyTicket, onAgentOwnerResolved);
        }

        return {
            agentId: centralAgentId,
            ref: centralSnapshot.ref,
            source: 'central',
            ticket: centralTicket,
        };
    }

    const legacyTicket = await findLegacyTicket(ticketId);

    if (!legacyTicket) {
        return null;
    }

    return {
        agentId: legacyTicket.agentId,
        ref: legacyTicket.ref,
        source: 'legacy',
        ticket: legacyTicket.ticket,
    };
};

const EditAvisPage = (props) => {

    const [loadingCompleted, setLoadingCompleted] = useState(false);

    const [carBrand, setCarBrand] = useState('');
    const [carModel, setCarModel] = useState('');
    const [carPlate, setCarPlate] = useState('');
    const [carColor, setCarColor] = useState('');
    const [carSerial, setCarSerial] = useState('');

    const [ticketImages, setTicketImages] = useState([]);
    const [ticketID, setTicketid] = useState('');
    const [ticketAgent, setTicketAgent] = useState('');
    const [ticketStatus, setTicketStatus] = useState('');
    const [ticketContract, setTicketContract] = useState('');
    const [ticketClient, setTicketClient] = useState('');
    const [ticketAddress, setTicketAddress] = useState('');
    const [ticketCity, setTicketCity] = useState('');
    const [ticketState, setTicketState] = useState('');
    const [ticketPostalCode, setTicketPostalCode] = useState('');
    const [ticketCountry, setTicketCountry] = useState('');
    const [ticketPrice, setTicketPrice] = useState('');
    const [ticketPrice7Days, setTicketPrice7Days] = useState('');
    const [ticketPrice30Days, setTicketPrice30Days] = useState('');
    const [ticketPayment, setTicketPayment] = useState('unpaid');
    const [ticketPaymentMethod, setTicketPaymentMethod] = useState('');
    const [originalPaymentStatus, setOriginalPaymentStatus] = useState('unpaid');
    const [ticketPaymentAmount, setTicketPaymentAmount] = useState('');
    const [ticketPaymentDate, setTicketPaymentDate] = useState('');
    const [ticketRule, setTicketRule] = useState('');

    const [editMode, setEditMode] = useState(false);


    const [ticketDate, setTicketDate] = useState('');
    const [ticketTime, setTicketTime] = useState('');
    const [ticketNote, setTicketNote] = useState('');
    const [ticketNoteAdmin, setTicketNoteAdmin] = useState('');
    const { register, handleSubmit, formState: { errors } } = useForm();



    const onEditSubmit = (data) => {
        alert(data)
    }


    useEffect(() => {

        let active = true;
        let liveTicketRef = null;
        let liveTicketHandler = null;

        if (props.avis != '') {
            getTicketByID(props.avis, () => active).then(ticketResult => {
                if (!active || !ticketResult || !ticketResult.ref || typeof ticketResult.ref.on !== 'function') {
                    return;
                }

                liveTicketRef = ticketResult.ref;
                liveTicketHandler = snapshot => {
                    if (!active || !snapshot.exists()) {
                        return;
                    }

                    setTicketImages(getTicketImages(snapshot.val()));
                };
                liveTicketRef.on('value', liveTicketHandler);
            });
        }

        return () => {
            active = false;

            if (liveTicketRef && liveTicketHandler && typeof liveTicketRef.off === 'function') {
                liveTicketRef.off('value', liveTicketHandler);
            }
        };
    }, [props.avis]);


    const onSubmit = async (e) => {
        e.preventDefault();
        return confirmAndSaveAdminTicket({
            confirm: () => SweetAlert.fire({
                title: `Avis n° ${props.avis}`,
                text: `Voulez vous sauvegarder les changements ?`,
                icon: 'warning',
                showCancelButton: true,
                reverseButtons: true,
            }),
            save: updateTicket,
            onSuccess: () => {
                setEditMode(false);
                toast.info(`L'avis n° ${props.avis} a été modifié avec succès`);
            },
            onError: () => {
                toast.error("Impossible de sauvegarder l'avis. Veuillez réessayer.");
            },
        });
    };

    async function updateTicket() {

        const updateFields = {
            address: ticketAddress,
            city: ticketCity,
            postalcode: ticketPostalCode,
            country: ticketCountry,
            state: ticketState,
            agent: ticketAgent,
            carbrand: carBrand,
            carcolor: carColor,
            carmodel: carModel,
            carplate: carPlate,
            carserial: carSerial,
            contract: ticketContract,
            contractname: ticketClient,
            date: ticketDate,
            note: ticketNote,
            noteadmin: ticketNoteAdmin ? ticketNoteAdmin : '' ,
            price: ticketPrice,
            price30days: ticketPrice30Days,
            price7days: ticketPrice7Days,
            rule: ticketRule,
            status: ticketStatus,
            time: ticketTime,
        };

        const isManualPayment = ticketPayment === 'paid' && originalPaymentStatus !== 'automatic';
        const isManualPaymentRemoval = ticketPayment === 'unpaid' &&
            MANUAL_PAYMENT_METHODS.has(originalPaymentStatus);

        if (isManualPayment && (!MANUAL_PAYMENT_METHODS.has(ticketPaymentMethod) ||
            !ticketPaymentAmount.trim() || !ticketPaymentDate)) {
            throw new Error('Le mode, le montant et la date du paiement sont obligatoires.');
        }

        const manualPayment = isManualPayment
            ? {
                status: 'paid',
                method: ticketPaymentMethod,
                amount: ticketPaymentAmount.trim(),
                date: ticketPaymentDate,
            }
            : isManualPaymentRemoval
                ? { status: 'unpaid' }
                : null;

        const result = await saveAdminTicketUpdate(
            ticketID,
            updateFields,
            manualPayment ? { manualPayment } : undefined
        );
        setOriginalPaymentStatus(isManualPayment ? ticketPaymentMethod : ticketPayment);
        return result;
    }


    const getTicketByID = async (pSearchID, isActive = () => true) => {

        setLoadingCompleted(false);

        try {
            const ticketResult = await resolveTicketForEdit(pSearchID);

            if (!isActive()) {
                return null;
            }

            if (ticketResult) {
                hydrateTicket(ticketResult.ticket, ticketResult.agentId, ticketResult.ref);
                return ticketResult;
            }

            window.location.href = "/dashboard/avis/";
            return null;
        } catch (error) {
            console.error("Erreur de chargement de l'avis", error);
            toast.error("Impossible de charger l'avis. Veuillez réessayer.");
            window.location.href = "/dashboard/avis/";
            return null;
        }
    };

    function hydrateTicket(foundTicket) {
        setTicketImages(getTicketImages(foundTicket));
        setTicketid(foundTicket.ticketid);

        setTicketStatus(foundTicket.status);
        setTicketAgent(foundTicket.agent);
        setTicketAddress(foundTicket.address);
        setTicketCity(foundTicket.city);
        setTicketPostalCode(foundTicket.postalcode);
        setTicketState(foundTicket.state);
        setTicketCountry(foundTicket.country);
        setTicketClient(foundTicket.contractname)

        setTicketContract(foundTicket.contract)
        setCarBrand(foundTicket.carbrand);
        setCarModel(foundTicket.carmodel);
        setCarColor(foundTicket.carcolor);
        setCarPlate(foundTicket.carplate);
        setCarSerial(foundTicket.carserial);

        setTicketDate(foundTicket.date);
        setTicketTime(foundTicket.time);
        setTicketRule(foundTicket.rule);
        setTicketPrice(foundTicket.price);
        setTicketPrice7Days(foundTicket.price7days);
        setTicketPrice30Days(foundTicket.price30days);
        const paymentStatus = getPaymentFormStatus(foundTicket.payment);
        setTicketPayment(paymentStatus === 'unpaid' ? 'unpaid' : 'paid');
        setTicketPaymentMethod(MANUAL_PAYMENT_METHODS.has(paymentStatus) ? paymentStatus : '');
        setOriginalPaymentStatus(paymentStatus);
        setTicketPaymentAmount(foundTicket.payment?.source === 'manual'
            ? String(foundTicket.payment.paidprice || '')
            : '');
        setTicketPaymentDate(foundTicket.payment?.source === 'manual'
            ? String(foundTicket.payment.paymentdate || '').slice(0, 10)
            : '');

        setTicketNote(foundTicket.note);
        setTicketNoteAdmin(foundTicket.noteadmin);

        setLoadingCompleted(true);
    }


    return (
        <Fragment>
            <Card>
                <CardBody>

                    <Col md="12">
                        <Row>

                        </Row>

                    </Col>
                    <Form onSubmit={onSubmit}>
                        <Row>
                            {!loadingCompleted
                                
                                ?
                                <><p class='p-t-30 text-center text-bold'>Veuillez ne pas quitter la page s.v.p. <br /></p><p class='p-t-0 text-center'>Affichage de l'avis : {props.avis} </p><div className={`loader-box loading-paymodal`}>
                                    <div class='loader-container paymodal-loading-container m-t-40 m-b-40'>
                                        <div class='loader-box paymodal-loader-box'>
                                            <div class='paycard-loader'></div>
                                        </div>
                                    </div>
                                </div></>

                                : <><Col md="4">

                                    <Row>
                                        <Card>
                                            <CardBody>
                                                <H4 attrH4={{ className: 'card-title mb-0 m-b-20' }}>Informations sur le véhicule</H4>
                                                <div className="profile-title">
                                                    <div className="media m-l-0">
                                                        <TicketPhotoGallery
                                                            images={ticketImages}
                                                            className="m-l-0"
                                                            imageClassName="paymodal-img"
                                                        />

                                                        <div className="media-body">
                                                            <H5 className="">Photos ({ticketImages.length}): <span className={'mb-1 f-20 txt-primary'}>{ticketID}</span></H5>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-footer">
                                                    <Btn attrBtn={{ className: 'btn-block m-b-20', color: 'primary', type: 'button' }}>Gérer les photos</Btn>
                                                </div>

                                                <Row>
                                                    <Col xs="6" sm="6" md="6">
                                                        <FormGroup><Label className="form-label">Marque</Label>
                                                            <Input className="form-control" type="text" disabled={editMode ? false : true} value={carBrand} onChange={e => setCarBrand(e.target.value)} />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs="6" sm="6" md="6">
                                                        <FormGroup><Label className="form-label">Modèle</Label>
                                                            <Input className="form-control" type="text" disabled={editMode ? false : true} value={carModel} onChange={e => setCarModel(e.target.value)} />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs="6" sm="6" md="6">
                                                        <FormGroup><Label className="form-label">Couleur</Label>
                                                            <Input type="select" name="select" className="form-control btn-square" disabled={editMode ? false : true} onChange={e => setCarColor(e.target.value)}>
                                                                {avisCarColorList.map((items, i) => items == carColor ? <option key={i} selected>{items}</option> : <option key={i}>{items}</option>
                                                                )}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs="6" sm="6" md="6">
                                                        <FormGroup><Label className="form-label">Plaque</Label>
                                                            <Input className="form-control" type="text" disabled={editMode ? false : true} value={carPlate} onChange={e => setCarPlate(e.target.value)} />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="12">
                                                        <FormGroup><Label className="form-label">Numéro de Série</Label>
                                                            <Input className="form-control" type="text" disabled={editMode ? false : true} value={carSerial} onChange={e => setCarSerial(e.target.value)} />
                                                        </FormGroup>
                                                    </Col>

                                                    <Col md="12">
                                                        <div> <Label className="form-label m-t-10">Note Interne</Label>
                                                            <Input type="textarea" className="form-control" disabled={editMode ? false : true} placeholder="Note pour l'équipe Maxpark" rows="3" value={ticketNoteAdmin} onChange={e => setTicketNoteAdmin(e.target.value)} />
                                                        </div>
                                                    </Col>
                                                </Row>

                                            </CardBody>
                                        </Card>
                                    </Row>
                                </Col><Col md="8">

                                        <Row className="m-l-20">
                                            <H4 attrH4={{ className: 'card-title mb-0 m-b-20' }}>Informations sur l'avis</H4>
                                            <Row>
                                                <Col xs="6" sm="6" md="6">
                                                    <FormGroup className="mb-6">
                                                        <Label className="form-label">Agent</Label>
                                                        <Input className="form-control" type="text" placeholder="Client" disabled={editMode ? false : true} value={ticketAgent} onChange={e => setTicketAgent(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="6">
                                                    <FormGroup className="mb-6">
                                                        <Label className="form-label">Statut</Label>
                                                        <Input type="select" name="select" className="form-control btn-square" disabled={editMode ? false : true} defaultValue={ticketStatus} onChange={e => setTicketStatus(e.target.value)}>
                                                            {avisStatusList.map((items, i) => items == ticketStatus ? <option key={i} selected>{items}</option> : <option key={i}>{items}</option>
                                                            )}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                <Col xs="6" sm="6" md="6">
                                                    <FormGroup className="mb-6">
                                                        <Label className="form-label">Client</Label>
                                                        <Input className="form-control" type="text" placeholder="Client" disabled={editMode ? false : true} value={ticketContract} onChange={e => setTicketContract(e.target.value)} />
                                                    </FormGroup>
                                                </Col>

                                                <Col xs="6" sm="6" md="6">
                                                    <FormGroup className="mb-6">
                                                        <Label className="form-label">Contrat</Label>
                                                        <Input className="form-control" type="text" placeholder="Client" disabled={editMode ? false : true} value={ticketClient} onChange={e => setTicketClient(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="6">
                                                    <FormGroup><Label className="form-label">Date</Label>
                                                        <Input className="form-control" type="text" placeholder="Date" disabled={editMode ? false : true} value={ticketDate} onChange={e => setTicketDate(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="6">
                                                    <FormGroup><Label className="form-label">Heure</Label>
                                                        <Input className="form-control" type="text" placeholder="Heure" disabled={editMode ? false : true} value={ticketTime} onChange={e => setTicketTime(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="12">
                                                    <FormGroup><Label className="form-label">{Address}</Label>
                                                        <Input className="form-control" type="text" placeholder="Adresse" disabled={editMode ? false : true} value={ticketAddress} onChange={e => setTicketAddress(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="3">
                                                    <FormGroup> <Label className="form-label">{City}</Label>
                                                        <Input className="form-control" type="text" placeholder="Ville" disabled={editMode ? false : true} value={ticketCity} onChange={e => setTicketCity(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="3">
                                                    <FormGroup><Label className="form-label">{PostalCode}</Label>
                                                        <Input className="form-control" type="text" placeholder="Code postal" disabled={editMode ? false : true} value={ticketPostalCode} onChange={e => setTicketPostalCode(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="3">
                                                    <FormGroup><Label className="form-label">{State}</Label>
                                                        <Input type="select" name="select" className="form-control btn-square" disabled={editMode ? false : true} defaultValue={ticketState} value={ticketState} onChange={e => setTicketState(e.target.value)}>
                                                            {UsersStateMenu.map((items, i) => items == ticketState ? <option key={i} selected>{items}</option> : <option key={i}>{items}</option>
                                                            )}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="6" md="3">
                                                    <FormGroup><Label className="form-label">{Country}</Label>
                                                        <Input type="select" name="select" className="form-control btn-square" disabled={editMode ? false : true} defaultValue={ticketCountry} value={ticketCountry} onChange={e => setTicketCountry(e.target.value)}>
                                                            {UsersCountryMenu.map((items, i) => items == ticketCountry ? <option key={i} selected>{items}</option> : <option key={i}>{items}</option>
                                                            )}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                <Col md="12">
                                                    <FormGroup><Label className="form-label">Règlement</Label>
                                                        <Input className="form-control" type="text" disabled={editMode ? false : true} onChange={e => e.lenghth >= 0 ? setTicketRule(e.target.value) : ""} value={ticketRule} />
                                                    </FormGroup>
                                                </Col>

                                                <Col xs="6" sm="3" md="3">
                                                    <FormGroup><Label className="form-label">Prix régulier</Label>
                                                        <Input className="form-control" type="text" disabled={editMode ? false : true} onChange={e => setTicketPrice(e.target.value)} value={ticketPrice} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="3" md="3">
                                                    <FormGroup><Label className="form-label">Prix 7 jours</Label>
                                                        <Input className="form-control" type="text" disabled={editMode ? false : true} value={ticketPrice7Days} onChange={e => setTicketPrice7Days(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="6" sm="3" md="3">
                                                    <FormGroup><Label className="form-label">Prix 30 jours</Label>
                                                        <Input className="form-control" type="text" disabled={editMode ? false : true} value={ticketPrice30Days} onChange={e => setTicketPrice30Days(e.target.value)} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs="12" sm="4" md="4">
                                                    <FormGroup>
                                                        <Label className="form-label" htmlFor="ticket-payment-status">Payé</Label>
                                                        <Input
                                                            id="ticket-payment-status"
                                                            type="select"
                                                            className="form-control btn-square"
                                                            disabled={!editMode || originalPaymentStatus === 'automatic'}
                                                            value={ticketPayment}
                                                            onChange={e => setTicketPayment(e.target.value)}
                                                        >
                                                            <option value="unpaid">Non payé</option>
                                                            <option value="paid">Payé</option>
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                {ticketPayment === 'paid' && originalPaymentStatus !== 'automatic' && <>
                                                    <Col xs="6" sm="4" md="4">
                                                        <FormGroup>
                                                            <Label className="form-label" htmlFor="ticket-payment-method">Mode de paiement</Label>
                                                            <Input
                                                                id="ticket-payment-method"
                                                                type="select"
                                                                className="form-control btn-square"
                                                                disabled={!editMode}
                                                                required
                                                                value={ticketPaymentMethod}
                                                                onChange={e => setTicketPaymentMethod(e.target.value)}
                                                            >
                                                                <option value="">--Sélectionner--</option>
                                                                <option value="cheque">Chèque</option>
                                                                <option value="interac">Virement Interac</option>
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs="6" sm="4" md="4">
                                                        <FormGroup>
                                                            <Label className="form-label" htmlFor="ticket-payment-amount">Montant payé</Label>
                                                            <Input
                                                                id="ticket-payment-amount"
                                                                className="form-control"
                                                                type="text"
                                                                inputMode="decimal"
                                                                disabled={!editMode}
                                                                required
                                                                value={ticketPaymentAmount}
                                                                onChange={e => setTicketPaymentAmount(e.target.value)}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs="6" sm="4" md="4">
                                                        <FormGroup>
                                                            <Label className="form-label" htmlFor="ticket-payment-date">Date du paiement</Label>
                                                            <Input
                                                                id="ticket-payment-date"
                                                                className="form-control"
                                                                type="date"
                                                                disabled={!editMode}
                                                                required
                                                                value={ticketPaymentDate}
                                                                onChange={e => setTicketPaymentDate(e.target.value)}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </>}
                                                <Col md="12">
                                                    <div> <Label className="form-label">Note</Label>
                                                        <Input type="textarea" className="form-control" disabled={editMode ? false : true} rows="3" value={ticketNote} onChange={e => setTicketNote(e.target.value)} />
                                                    </div>
                                                </Col>
                                            </Row>

                                        </Row>
                                    </Col><div className="form-footer">
                                        <button type='button' className='btn btn-secondary btn-block m-t-30 pull-left' onClick={e => setEditMode(!editMode)}>{!editMode ? i18n.t('Dashboard_Common_Edit') : i18n.t('Dashboard_Common_Cancel')} </button>
                                        <button type='submit' disabled={editMode ? false : true} className='btn btn-primary btn-maxpark btn-block m-t-30 pull-right'>{SaveTxt}</button>
                                    </div></>

                            }
                  

                            
                        </Row>

                    </Form>

                </CardBody>
            </Card>



        </Fragment>
    );
};
export default EditAvisPage;
